import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AutocompleteOptions,
  DateState,
  FilterStateType,
  FullScreenLoader,
  MainSectionLayout,
  ReportConfig,
  ReportData,
} from '@Components/index';
import { CommonConstants, RoutesConstants } from '@Constants/index';
import AdminCallLog from '@Containers/Report/AdminCallLog';
import AdminDialSessions from '@Containers/Report/AdminDialSessions';
import AdminEmailLog from '@Containers/Report/AdminEmailLog';
import { useGetSessionsQuery } from '@Services/report.service';
import { useGetUsersListQuery } from '@Services/users.service';
import {
  BaseAlertProps,
  ReportTypes,
  VoiceProviderTypes,
} from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import useRoute from '@Utils/hooks/useRoute.hook';
import {
  getAccounts,
  getAccountsRTKQuery,
  getSessions,
  getSessionsRTKQuery,
} from '@Utils/report/report.utils';

import ActiveSessions from '../ActiveSessions';

const AdminReportConfiguration: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { changeRoute } = useRoute();
  const { triggerAlert } = useAlert();

  /** PAGE HEADING */
  const heading = t('reports.Report Configuration');

  /** Memoised Initial Filter State
   * This will prevent re-render when used in useEffect as a dependency
   */
  const initialFilterState: FilterStateType = useMemo(
    () => ({
      status: [],
      disposition: [],
      callStatus: [],
    }),
    []
  );

  /** STATES */
  const [reportType, setReportType] = useState<ReportTypes | null>(
    query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) as ReportTypes
  );
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [filterState, setFilterState] =
    useState<FilterStateType>(initialFilterState);
  const [sessionAutocompleteValue, setSessionAutocompleteValue] = useState<
    AutocompleteOptions[]
  >([]);
  const [accountAutocompleteValue, setAccountAutocompleteValue] = useState<
    AutocompleteOptions[]
  >([]);
  /** STATES */
  const [voiceProviderType, setVoiceProviderType] =
    useState<VoiceProviderTypes>(
      (query.get(
        CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER
      ) as VoiceProviderTypes) || VoiceProviderTypes.Both
    );
  const [accountCount, setAccountCount] = useState<number>(0);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** RTK QUERY HOOKS --> */
  /** GET ACCOUNTS */
  const {
    data: accountsData,
    isLoading: accountsIsLoading,
    isFetching: accountsIsFetching,
    error: accountsError,
  } = useGetUsersListQuery(
    useMemo(() => getAccountsRTKQuery(accountCount), [accountCount])
  );

  /** GET SESSIONS */
  const {
    data: sessionsData,
    isFetching: sessionsIsFetching,
    isLoading: sessionsIsLoading,
    error: sessionsError,
  } = useGetSessionsQuery(getSessionsRTKQuery(accountAutocompleteValue), {
    skip: !accountAutocompleteValue.length,
  });
  /** <-- RTK QUERY HOOKS */

  /** EDGE CASE
   * Adds default query params to the url if not added (pageNo, pageSize, order, direction).
   * */
  useEffect(() => {
    if (reportType) {
      const routeParams = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
          CommonConstants.INITIAL_PAGE_NO,
        [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
          CommonConstants.INITIAL_ROWS_PER_PAGE,
        ...(reportType === ReportTypes.Sessions && {
          [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
            query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
        }),
        ...(reportType === ReportTypes.ActiveSessions && {
          [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
            query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.LAST_CALL,
        }),
        ...(reportType !== ReportTypes.CallLog && {
          [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
            query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
            CommonConstants.INITIAL_SORT_DIRECTION,
        }),
      });

      changeRoute(routeParams);
    }
  }, [changeRoute, reportType]);

  /** Fetches All the Accounts */
  useEffect(() => {
    if (accountCount !== accountsData?.data?.length) {
      setAccountCount(accountsData?.total || 0);
    }
  }, [accountsData, accountCount]);

  /** COMBINED VARIABLES --> */
  const error = accountsError || sessionsError;
  /** <-- COMBINED VARIABLES */

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** Filter Options (Accounts & Sessions) */
  const accountsOptions = getAccounts(accountsData?.data);
  const sessionsOptions = accountAutocompleteValue.length
    ? getSessions(sessionsData)
    : [];

  /** Handles Reset Btn Click (resets all the filters) */
  const resetFilters = useCallback(() => {
    setFilterState({ ...initialFilterState });
    setDateState((prevState) => [
      { ...prevState[0], startDate: undefined, endDate: undefined },
    ]);
    setAccountAutocompleteValue([]);
    setSessionAutocompleteValue([]);
    setVoiceProviderType(VoiceProviderTypes.Both);
  }, [initialFilterState]);

  useEffect(() => {
    setReportType(
      query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) as ReportTypes
    );

    if (!reportType && !location.search) {
      history.push(RoutesConstants.ADMIN_ROUTES.REPORT);
      resetFilters();
    }
  }, [location.search, history, reportType, resetFilters]);

  /** Changes the reportType with selected report filters & type and updates query params. */
  const generateBtnClickHandler = (reportData: ReportData) => {
    const {
      type,
      accounts = [],
      sessions,
      voiceProvider,
      filters: { callStatus, status, disposition },
      startDate,
      endDate,
    } = reportData;

    setReportType(type);

    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE]: type,
      [CommonConstants.QUERY_PARAMS.REPORT.USER_IDS]: accounts,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        CommonConstants.INITIAL_ROWS_PER_PAGE,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        CommonConstants.INITIAL_PAGE_NO,
      [CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID]: sessions,
      [CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION]: disposition,
      ...(type !== ReportTypes.EmailLog && {
        [CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER]: voiceProvider,
      }),
      [CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS]: callStatus,
      [CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE]: status,
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
      ...(type === ReportTypes.Sessions && {
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DATE,
      }),
      ...(type === ReportTypes.ActiveSessions && {
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.LAST_CALL,
      }),
      ...(type !== ReportTypes.CallLog && {
        [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
          CommonConstants.INITIAL_SORT_DIRECTION,
      }),
    });

    changeRoute(searchParam);
  };

  /** New Report Button Click Handler */
  const generateNewReport = () => {
    changeRoute();
    setReportType(null);
  };

  /** Renders Report Container according to Report Type (CallLog Report/Session Summary) */
  const getReportScreen = () => {
    switch (reportType) {
      case ReportTypes.CallLog:
        return (
          <AdminCallLog newReportBtnProps={{ onClick: generateNewReport }} />
        );
      case ReportTypes.Sessions:
        return (
          <AdminDialSessions
            newReportBtnProps={{ onClick: generateNewReport }}
          />
        );
      case ReportTypes.ActiveSessions:
        return <ActiveSessions />;
      case ReportTypes.EmailLog:
        return (
          <AdminEmailLog newReportBtnProps={{ onClick: generateNewReport }} />
        );
      default:
        return (
          <MainSectionLayout
            alertState={alertState}
            heading={heading}
            filters={
              <ReportConfig
                type="report"
                filterState={filterState}
                setFilterState={setFilterState}
                accountAutocompleteProps={{
                  options:
                    accountsOptions.length === accountCount
                      ? accountsOptions
                      : [],
                  limitTags: CommonConstants.LIMIT_TAGS,
                  accountAutocompleteValue,
                  setAccountAutocompleteValue,
                  loading:
                    accountsIsLoading || accountsIsFetching || !accountCount,
                }}
                sessionAutocompleteProps={{
                  options: sessionsOptions,
                  limitTags: CommonConstants.LIMIT_TAGS,
                  sessionAutocompleteValue,
                  setSessionAutocompleteValue,
                  loading: sessionsIsLoading || sessionsIsFetching,
                }}
                dateRangeProps={{
                  dateState,
                  setDateState,
                }}
                primaryBtnProps={{
                  text: t('common.messages.Generate'),
                  onClick: generateBtnClickHandler,
                }}
                secondaryBtnProps={{
                  text: t('common.messages.Reset All'),
                  onClick: resetFilters,
                }}
                voiceProviderProps={voiceProviderType}
                setVoiceProviderProps={setVoiceProviderType}
              />
            }
          />
        );
    }
  };

  return accountsIsLoading ? <FullScreenLoader /> : getReportScreen();
};

export default AdminReportConfiguration;
